<template>
    <section v-if="canView" class="employee-details-section employee-ppe-sizes">
        <div class="employee-details-section__header">
            <h2><i class="fas fa-tshirt" aria-hidden="true"></i> Размеры СИЗ</h2>
            <b-button v-if="canManage && !editing" size="sm" variant="outline-primary" @click="openEditor">
                <i class="fas fa-edit mr-1" aria-hidden="true"></i>Изменить
            </b-button>
        </div>
        <b-form v-if="editing && canManage" @submit.prevent="save">
            <b-row>
                <b-col v-for="field in fields" :key="field.key" cols="12" :md="field.key === 'notes' ? 12 : 6">
                    <b-form-group :label="field.label" :label-for="`ppe-${field.key}`">
                        <AppTextarea v-if="field.key === 'notes'" :id="`ppe-${field.key}`"
                            v-model="form[field.key]" :maxlength="field.limit" :disabled="pending" />
                        <b-form-input v-else :id="`ppe-${field.key}`" v-model="form[field.key]"
                            :maxlength="field.limit" :placeholder="field.example" :disabled="pending" />
                    </b-form-group>
                </b-col>
            </b-row>
            <b-alert :show="Boolean(error)" variant="danger" role="alert">{{ error }}</b-alert>
            <div class="d-flex flex-wrap">
                <b-button type="submit" variant="primary" size="sm" class="mr-2" :disabled="pending">
                    {{ pending ? 'Сохранение…' : 'Сохранить' }}
                </b-button>
                <b-button type="button" variant="outline-secondary" size="sm" :disabled="pending" @click="cancel">
                    Отмена
                </b-button>
            </div>
        </b-form>
        <dl v-else-if="filledFields.length" class="employee-details-record__facts">
            <template v-for="field in filledFields">
                <dt :key="`${field.key}-label`">{{ field.label }}</dt>
                <dd :key="field.key" class="employee-ppe-sizes__value">{{ sizes[field.key] }}</dd>
            </template>
        </dl>
        <p v-else class="text-muted mb-0">Размеры СИЗ не указаны</p>
        <EmployeePpeShipments :employee-id="employee.employee_id" :context-params="contextParams" />
    </section>
</template>

<script>
import api from '@/utils/api';
import EmployeePpeShipments from './EmployeePpeShipments.vue';
import {PPE_FORM_FIELDS} from '@/utils/ppeSizes';

export default {
    name: 'EmployeePpeSizes',
    components: {EmployeePpeShipments},
    props: {
        employee: {type: Object, required: true},
        contextParams: {type: Object, default: () => ({})}
    },
    data() {
        return {fields: PPE_FORM_FIELDS, form: {}, original: null, editing: false, pending: false, error: '', requestId: 0};
    },
    computed: {
        access() { return this.employee.permissions && this.employee.permissions.card_access || {}; },
        canView() { return Boolean(this.access.view && this.access.view.ppe_sizes); },
        canManage() { return Boolean(this.access.manage && this.access.manage.ppe_sizes); },
        sizes() { return this.employee.ppe_sizes || {}; },
        filledFields() { return this.fields.filter(field => this.sizes[field.key]); }
    },
    watch: {
        'employee.employee_id'() { this.cancel(); },
        canManage(value) { if (!value) this.cancel(); }
    },
    methods: {
        openEditor() {
            this.original = this.employee.ppe_sizes ? Object.assign({}, this.employee.ppe_sizes) : null;
            this.form = this.fields.reduce((result, field) => {
                result[field.key] = this.sizes[field.key] || '';
                return result;
            }, {});
            this.error = '';
            this.editing = true;
        },
        cancel() {
            this.requestId += 1;
            this.editing = false;
            this.pending = false;
            this.form = {};
            this.original = null;
            this.error = '';
        },
        async save() {
            if (this.pending || !this.canManage) return;
            const requestId = ++this.requestId;
            const employeeId = this.employee.employee_id;
            this.pending = true;
            this.error = '';
            try {
                await api.patch(`/api/employees/${encodeURIComponent(employeeId)}`, {
                    ppe_sizes: this.form,
                    expected_ppe_sizes: this.original
                }, {params: this.contextParams, ignoreModal: true});
                if (requestId !== this.requestId) return;
                this.cancel();
                this.$emit('saved');
            } catch (error) {
                if (requestId !== this.requestId) return;
                const data = error.response && error.response.data;
                this.error = data && (data.msg || data.message) || 'Не удалось сохранить размеры СИЗ';
            } finally {
                if (requestId === this.requestId) this.pending = false;
            }
        }
    }
};
</script>

<style scoped>
.employee-ppe-sizes {
    order: 5;
}
.employee-details-section__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 12px;
}
.employee-details-section__header h2 {
    margin: 0;
    color: #34495d;
    font-size: 0.98rem;
    font-weight: 700;
}
.employee-details-section__header h2 i {
    width: 19px;
    margin-right: 5px;
    color: #6688a8;
    text-align: center;
}
.employee-details-record__facts {
    display: grid;
    grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
    gap: 4px 9px;
    margin: 9px 0 0;
    font-size: 0.72rem;
}
.employee-details-record__facts dt {
    margin: 0;
    color: #8793a0;
    font-weight: 400;
}
.employee-details-record__facts dd {
    margin: 0;
    color: #46596b;
    font-weight: 600;
}
.employee-ppe-sizes__value {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
}
</style>
